import { useState } from 'react';
import {
  Accordion as LUIAccordion,
  AccordionGroup as LUIAccordionGroup,
  AccordionGroupVariant,
} from '@lendi-ui/accordion';
import getColour, { ValidHex } from '@lendi-ui/commons/colours';
import type { IconProps } from '@lendi-ui/icon';
import Add from '@lendi-ui/icon/Add';

import { useAnalytics } from '@funnels/providers';

import { AddButton } from '../style';
import { AccordionHeaderText, AccordionSubHeaderText, ColouredCircle, PrimaryButton } from './style';

interface AccordionGroupProps {
  variant?: AccordionGroupVariant;
  children?: React.ReactNode;
  addButtonText?: string;
  onAdd?: () => void;
  shouldDisableAddButton?: boolean;
}

export const AccordionGroup = ({
  variant = 'empty',
  children,
  addButtonText = 'Add',
  shouldDisableAddButton = false,
  onAdd,
}: AccordionGroupProps) => (
  <>
    <LUIAccordionGroup data-component="accordion-group" variant={variant}>
      {children}
    </LUIAccordionGroup>
    {onAdd && (
      <AddButton
        variant="empty"
        before={<Add color={getColour('interaction', 'main')} width="1.5em" height="1.5em" />}
        onClick={onAdd}
        isDisabled={shouldDisableAddButton}
        isFullWidth
        data-component="add-button"
      >
        {addButtonText}
      </AddButton>
    )}
  </>
);

interface AccordionProps {
  headerText: string;
  headerSubText?: string;
  showHeaderSubTextWhenOpen?: boolean;
  headerAfter?: React.ReactElement<IconProps>;
  children?: React.ReactNode;
  primaryButtonLabel?: string;
  onPrimaryButtonClick?: () => void;
  isCompleted?: boolean;
  headerClassName?: string;
  shouldWrapHeader?: boolean;
}

export const Accordion = (props: AccordionProps) => {
  const { tracking } = useAnalytics();
  const {
    headerText,
    headerSubText,
    showHeaderSubTextWhenOpen = false,
    headerAfter,
    children,
    primaryButtonLabel = 'Delete',
    onPrimaryButtonClick,
    isCompleted,
    headerClassName,
    shouldWrapHeader = false,
  } = props;
  const [isOpen, setIsOpen] = useState(true);

  const completionIndicatorColor: ValidHex = isCompleted ? getColour('success', 500) : getColour('error', 500);
  const headerAfterComponent =
    headerAfter ||
    (isCompleted !== undefined ? <ColouredCircle backgroundColour={completionIndicatorColor} /> : undefined);

  const toggleAccordionOpenState = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      tracking?.accordion.expanded({
        name: headerText,
        position: headerText,
      });
    } else {
      tracking?.accordion.collapsed({ name: headerText, position: headerText });
    }
  };

  return (
    <LUIAccordion isOpen={isOpen} data-component="accordion">
      <LUIAccordion.Header
        data-component="accordion-header"
        after={headerAfterComponent}
        onClick={toggleAccordionOpenState}
      >
        <AccordionHeaderText className={headerClassName} shouldWrap={shouldWrapHeader}>
          {headerText}
        </AccordionHeaderText>
        {(!isOpen || showHeaderSubTextWhenOpen) && headerSubText && (
          <AccordionSubHeaderText>{headerSubText}</AccordionSubHeaderText>
        )}
      </LUIAccordion.Header>
      <LUIAccordion.Content data-component="accordion-content">
        {children}
        {onPrimaryButtonClick && (
          <PrimaryButton
            data-component="accordion-primary-button"
            variant="primary"
            onClick={onPrimaryButtonClick}
            isInverse
          >
            {primaryButtonLabel}
          </PrimaryButton>
        )}
      </LUIAccordion.Content>
    </LUIAccordion>
  );
};
